import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  TextField,
  Switch,
  withStyles,
  Popover,
  IconButton,
  Slide,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DatePickerCustom from "../DatePickerCustom";
import { CloseXIcon } from "../ChatiaIcons";
import ChatiaDialogActions from "../ChatiaDialogActions";
import CustomToggleSwitch from "../CustomToggleSwitch";
import { i18n } from "../../translate/i18n";

// Select padrão — IDÊNTICO ao WhatsAppModal/Reports: MUI v4 outlined com
// dropdown estilizado por useFilterDropdownStyles (mesmo padrão Kanban/Reports).
const StandardSelect = ({ label, value, onChange, options, required, fullWidth = true }) => {
  const dropdownClasses = useFilterDropdownStyles();
  return (
    <FormControl
      variant="outlined"
      margin="dense"
      fullWidth={fullWidth}
      required={required}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        onChange={onChange}
        variant="outlined"
        margin="dense"
        label={label}
        MenuProps={{
          classes: { paper: dropdownClasses.paper },
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          getContentAnchorEl: null,
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const GreenSwitch = withStyles({
  root: { width: 36, height: 20, padding: 0, display: "flex", overflow: "visible" },
  switchBase: {
    padding: 2,
    color: "#fff !important",
    "&:hover": { backgroundColor: "transparent !important" },
    "&$checked": {
      transform: "translateX(16px)",
      color: "#fff !important",
      "&:hover": { backgroundColor: "transparent !important" },
      "& + $track": { opacity: "1 !important", backgroundColor: "#4caf50 !important" },
    },
  },
  thumb: { width: 16, height: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.2)", backgroundColor: "#fff !important" },
  track: { borderRadius: 10, opacity: "1 !important", backgroundColor: "#9e9e9e !important" },
  checked: {},
})(Switch);

// Gera horários de 30 em 30 min
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    TIME_OPTIONS.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}

const safeT = (key, fallback) => {
  const out = i18n.t(key);
  return !out || out === key ? fallback : out;
};
const getDurationOptions = () => [
  { value: 15, label: safeT("eventPopover.durations.min15", "15 minutos") },
  { value: 30, label: safeT("eventPopover.durations.min30", "30 minutos") },
  { value: 60, label: safeT("eventPopover.durations.hour1", "1 hora") },
  { value: 90, label: safeT("eventPopover.durations.hour1h30", "1h30") },
  { value: 120, label: safeT("eventPopover.durations.hour2", "2 horas") },
  { value: 180, label: safeT("eventPopover.durations.hour3", "3 horas") },
  { value: 240, label: safeT("eventPopover.durations.hour4", "4 horas") },
  { value: 480, label: safeT("eventPopover.durations.hour8", "8 horas") },
];

const getReminderOptions = () => [
  { value: 0, label: safeT("eventPopover.reminders.none", "Sem lembrete") },
  { value: 5, label: safeT("eventPopover.reminders.min5", "5 minutos antes") },
  { value: 10, label: safeT("eventPopover.reminders.min10", "10 minutos antes") },
  { value: 15, label: safeT("eventPopover.reminders.min15", "15 minutos antes") },
  { value: 30, label: safeT("eventPopover.reminders.min30", "30 minutos antes") },
  { value: 60, label: safeT("eventPopover.reminders.hour1", "1 hora antes") },
  { value: 120, label: safeT("eventPopover.reminders.hour2", "2 horas antes") },
  { value: 1440, label: safeT("eventPopover.reminders.day1", "1 dia antes") },
];

const getEventTypeOptions = () => [
  { value: "reuniao", label: safeT("eventPopover.eventTypes.meeting", "Reunião") },
  { value: "tarefa", label: safeT("eventPopover.eventTypes.task", "Tarefa") },
];

const PopoverCloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: "21px 21px 12px 12px",
    width: 380,
    maxWidth: "95vw",
    overflow: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    border: theme.mode === "dark"
      ? "1px solid rgba(255,255,255,0.04)"
      : "1px solid rgba(0,0,0,0.04)",
    background: "var(--base-color, " + (theme.mode === "dark" ? "#1a1a1a" : "#fff") + ")",
    // Herdar estilos de input do ChatiaDialog
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      backgroundColor: "transparent !important",
      backdropFilter: "none !important",
      WebkitBackdropFilter: "none !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme.mode === "light"
        ? "rgba(0,0,0,0.08)"
        : "rgba(255,255,255,0.08)") + " !important",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme.mode === "light"
        ? "rgba(0,0,0,0.15)"
        : "rgba(255,255,255,0.15)") + " !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "color-mix(in srgb, var(--base-text-color, inherit) 25%, transparent) !important",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "var(--base-text-color, inherit) !important",
    },
    "& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      paddingRight: "56px !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      right: "8px !important",
    },
    "& .MuiAutocomplete-popupIndicator": {
      padding: 4,
      color: theme.mode === "light" ? "#667781" : "#8696a0",
    },
    "& .MuiSelect-icon": {
      right: 8,
      top: "50%",
      transform: "translateY(-50%)",
    },
  },
  header: {
    ...theme.chatiaDS.header,
  },
  headerTitle: {
    ...theme.chatiaDS.headerTitle,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  closeButton: {
    backgroundColor: theme.chatiaDS.closeButton.backgroundColor,
    color: theme.chatiaDS.closeButton.color,
    transition: theme.chatiaDS.closeButton.transition,
    padding: 3,
  },
  content: {
    padding: "14px",
    maxHeight: "60vh",
    overflowY: "auto",
    ...theme.scrollbarStylesSoft,
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    borderRadius: 10,
    border: theme.palette.type === "dark" || theme.mode === "dark"
      ? "1px solid rgba(255,255,255,0.10)"
      : "1px solid rgba(0,0,0,0.10)",
    background: theme.palette.type === "dark" || theme.mode === "dark"
      ? "rgba(255,255,255,0.02)"
      : "transparent",
    marginTop: 4,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" || theme.mode === "dark"
        ? "rgba(255,255,255,0.05)"
        : "#f5f5f5",
      borderColor: theme.palette.type === "dark" || theme.mode === "dark"
        ? "rgba(255,255,255,0.20)"
        : "#bdbdbd",
    },
  },
  switchLabel: {
    fontSize: "0.9rem",
    fontWeight: 500,
    color: theme.palette.type === "dark" || theme.mode === "dark"
      ? "#e9edef"
      : "#424242",
    cursor: "pointer",
    userSelect: "none",
  },
  reminderHint: {
    fontSize: 11,
    color: theme.palette.primary.main,
    marginTop: 4,
  },
}));

const SlideFromRight = React.forwardRef((props, ref) => (
  <Slide direction="left" ref={ref} {...props} timeout={{ enter: 200, exit: 150 }} />
));
const SlideFromLeft = React.forwardRef((props, ref) => (
  <Slide direction="right" ref={ref} {...props} timeout={{ enter: 200, exit: 150 }} />
));

const EventPopover = ({ open, anchorEl, side = "right", onClose, onSave, initialDate, onTitleChange, editEvent }) => {
  const classes = useStyles();

  const isEditing = Boolean(editEvent);

  const [title, _setTitle] = useState("");
  const setTitle = (v) => {
    _setTitle(v);
    if (onTitleChange) onTitleChange(v);
  };
  const [eventType, setEventType] = useState("reuniao");
  const [date, setDate] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [startTime, setStartTime] = useState("09:00");
  const [duration, setDuration] = useState(60);
  const [reminder, setReminder] = useState(15);
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && editEvent) {
      _setTitle(editEvent.title || "");
      if (onTitleChange) onTitleChange(editEvent.title || "");
      setEventType(editEvent.eventType || "reuniao");
      setDate(editEvent.date || "");
      setAllDay(editEvent.allDay || false);
      setStartTime(editEvent.startTime || "09:00");
      setDuration(editEvent.duration || 60);
      setReminder(editEvent.reminder != null ? editEvent.reminder : 15);
      setLocation(editEvent.location || "");
      setDescription(editEvent.description || "");
    } else if (open && initialDate) {
      const d = initialDate;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      setDate(`${yyyy}-${mm}-${dd}`);
      if (d.getHours() !== 0 || d.getMinutes() !== 0) {
        setStartTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
      }
    }
  }, [open, initialDate, editEvent]);

  useEffect(() => {
    if (!open) {
      _setTitle("");
      if (onTitleChange) onTitleChange("");
      setEventType("reuniao");
      setDate("");
      setAllDay(false);
      setStartTime("09:00");
      setDuration(60);
      setReminder(15);
      setLocation("");
      setDescription("");
      setSaving(false);
    }
  }, [open]);

  // Verifica apenas se a data selecionada é anterior ao dia atual.
  const isDateInPast = (() => {
    if (!date) return false;
    const [y, m, d] = date.split("-").map(Number);
    if (!y || !m || !d) return false;
    const selectedDate = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate.getTime() < today.getTime();
  })();

  const handleSubmit = async () => {
    if (!title.trim() || !date || isDateInPast) return;
    setSaving(true);
    await onSave({
      title: title.trim(),
      eventType,
      date,
      allDay,
      startTime: allDay ? null : startTime,
      duration: allDay ? null : duration,
      reminder,
      location: location.trim(),
      description: description.trim(),
    });
    setSaving(false);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: side === "left" ? "left" : "right" }}
      transformOrigin={{ vertical: "top", horizontal: side === "left" ? "right" : "left" }}
      TransitionComponent={side === "left" ? SlideFromLeft : SlideFromRight}
      PaperProps={{ className: classes.paper }}
    >
      {/* Header */}
      <div className={`${classes.header} chatia-ds-header`}>
        <div className={classes.headerTitle}>
          {isEditing ? i18n.t("eventPopover.titleEdit") : i18n.t("eventPopover.titleNew")}
        </div>
        <IconButton size="small" onClick={onClose} className="chatia-close-btn">
          <CloseXIcon style={{ fontSize: 18 }} />
        </IconButton>
      </div>

      {/* Content */}
      <div className={classes.content}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className="chatia-field-required">
              <TextField variant="outlined" size="small" fullWidth label={i18n.t("eventPopover.fields.titleLabel")} placeholder={i18n.t("eventPopover.fields.titlePlaceholder")} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
            </div>
          </Grid>

          <Grid item xs={12}>
            <StandardSelect required label={i18n.t("eventPopover.fields.eventTypeLabel")} value={eventType} onChange={(e) => setEventType(e.target.value)} options={getEventTypeOptions()} />
          </Grid>

          <Grid item xs={12}>
            <div className="chatia-field-required">
              <DatePickerCustom label={i18n.t("eventPopover.fields.dateLabel")} value={date} onChange={(v) => setDate(v)} />
            </div>
          </Grid>

          <Grid item xs={12}>
            <div className={classes.switchRow}>
              <span className={classes.switchLabel} onClick={() => setAllDay(!allDay)}>{i18n.t("eventPopover.fields.allDay")}</span>
              <CustomToggleSwitch checked={allDay} onChange={() => setAllDay(!allDay)} />
            </div>
          </Grid>

          {!allDay && (
            <>
              <Grid item xs={eventType === "tarefa" ? 12 : 6}>
                <div className="chatia-field-required">
                <Autocomplete
                  freeSolo
                  size="small"
                  options={TIME_OPTIONS}
                  inputValue={startTime}
                  onInputChange={(e, v, reason) => {
                    if (reason === "reset") return;
                    // Auto-formata quando digitar 4 dígitos seguidos (ex: 0915 → 09:15)
                    const digits = v.replace(/\D/g, "");
                    if (digits.length >= 4 && !v.includes(":")) {
                      const hh = Math.min(parseInt(digits.slice(0, 2), 10), 23);
                      const mm = Math.min(parseInt(digits.slice(2, 4), 10), 59);
                      setStartTime(`${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
                      return;
                    }
                    setStartTime(v);
                  }}
                  onChange={(e, v) => { if (v) setStartTime(v); }}
                  disableClearable
                  getOptionLabel={(o) => o || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      margin="dense"
                      label={eventType === "tarefa" ? i18n.t("eventPopover.fields.timeLabel") : i18n.t("eventPopover.fields.startTimeLabel")}
                      placeholder="09:15"
                      InputLabelProps={{ shrink: true }}
                      onBlur={(e) => {
                        const v = e.target.value;
                        if (/^\d{1,2}:\d{2}$/.test(v.trim())) {
                          const [h, m] = v.trim().split(":").map(Number);
                          setStartTime(`${String(Math.min(h, 23)).padStart(2, "0")}:${String(Math.min(m, 59)).padStart(2, "0")}`);
                          return;
                        }
                        const raw = v.replace(/\D/g, "");
                        if (raw.length === 0) { setStartTime("09:00"); return; }
                        let hh, mm;
                        if (raw.length <= 2) {
                          hh = Math.min(parseInt(raw, 10) || 0, 23);
                          mm = 0;
                        } else {
                          hh = Math.min(parseInt(raw.slice(0, -2), 10) || 0, 23);
                          mm = Math.min(parseInt(raw.slice(-2), 10) || 0, 59);
                        }
                        setStartTime(`${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
                      }}
                    />
                  )}
                  ListboxProps={{ style: { maxHeight: 200 } }}
                />
                </div>
              </Grid>
              {eventType !== "tarefa" && (
                <Grid item xs={6}>
                  <StandardSelect required label={i18n.t("eventPopover.fields.durationLabel")} value={duration} onChange={(e) => setDuration(e.target.value)} options={getDurationOptions()} />
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            <StandardSelect label={i18n.t("eventPopover.fields.reminderLabel")} value={reminder} onChange={(e) => setReminder(e.target.value)} options={getReminderOptions()} />
            {reminder > 0 && (
              <div className={classes.reminderHint}>
                {i18n.t("eventPopover.reminderHint", { when: getReminderOptions().find(o => o.value === reminder)?.label?.toLowerCase() })}
              </div>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField variant="outlined" size="small" fullWidth label={i18n.t("eventPopover.fields.locationLabel")} placeholder={i18n.t("eventPopover.fields.locationPlaceholder")} value={location} onChange={(e) => setLocation(e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <TextField variant="outlined" size="small" fullWidth multiline minRows={3} label={i18n.t("eventPopover.fields.descriptionLabel")} placeholder={i18n.t("eventPopover.fields.descriptionPlaceholder")} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Grid>
        </Grid>
      </div>

      {/* Aviso de data no passado */}
      {isDateInPast && (
        <div style={{
          padding: "8px 14px",
          fontSize: 12,
          fontWeight: 500,
          color: "#ef4444",
          background: "rgba(239, 68, 68, 0.08)",
          borderTop: "1px solid rgba(239, 68, 68, 0.18)",
        }}>
          {i18n.t("eventPopover.dateInPast") || "A data e hora selecionadas estão no passado."}
        </div>
      )}

      {/* Footer */}
      <ChatiaDialogActions
        onClick={handleSubmit}
        disabled={!title.trim() || !date || isDateInPast || saving}
        loading={saving}
        saveText={isEditing ? i18n.t("eventPopover.buttons.save") : i18n.t("eventPopover.buttons.create")}
        type="button"
      />
    </Popover>
  );
};

export default EventPopover;
